import React, { useEffect, useMemo, useState } from 'react';
import { Adjust } from '@cloudinary/url-gen/actions/adjust';
import { useNavigate, useLocation } from 'react-router';
import { TwoColumnAppType, TwoColumnPropertiesType } from '@appTypes/CategoryAppType';
import { SiteStructureType } from '@appTypes/siteStructureType';
import FeedbackSurvey from '@components/feedbacksurvey/FeedbackSurvey';
import { Layout } from '@components/layout/Layout';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import ErrorBoundary from '@components/error-component/ErrorBoundary';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { TabContainer } from './TabContainer/TabContainer';
import * as S from './TwoColumnApp.styles';
import { HeroContent } from './HeroContent/HeroContent';

export const TwoColumnApp = ({ data, appData }: { data: SiteStructureType; appData: TwoColumnAppType }) => {
  const { heroImage, heroContent, darkenBackground, linkPreview, tabs } = appData.properties;

  // Handle query string for linking into specific tabs
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const updateSearchParams = (key: string, value: string | undefined) => {
    if (value) {
      searchParams.set(key, value);
      navigate({ search: searchParams.toString() });
    }
  };

  // Extract tabs for the right column
  const [rightColumnTabs, setRightColumnTabs] = useState<TwoColumnPropertiesType['tabs']>(
    tabs.filter((tab) => tab.column === 'right'),
  );
  useEffect(() => setRightColumnTabs(tabs.filter((tab) => tab.column === 'right')), [tabs]);

  // Fetch hero background image
  const contentUrl = useWindowLocation();
  const { getResponsiveImage } = useCloudinary();

  const backgroundImage = getResponsiveImage(heroImage?.publicId, {
    width: 1920,
    height: 1080,
    quality: 'auto:good',
  });
  if (backgroundImage && darkenBackground) {
    backgroundImage.adjust(Adjust.opacity(60)).backgroundColor('black');
  }

  // Memoise hero to prevent page bouncing on rerender
  const Hero = useMemo(() => <HeroContent heroContent={heroContent} />, [heroContent]);

  return (
    <Layout
      title={data.seoConfig?.title || data.name}
      description={data.seoConfig?.introduction || data.ingress || ''}
      gtmArgs={{ page_type: 'TwoColumnApp', pageCategory: data.slug, contentId: data.name }}
      imgUrl={linkPreview?.srcSet?.jpgLarge || undefined}
      internalSearchMetaData={{ cardType: InternalSearchCardType.CategoryApp }}
    >
      <S.PageContent $backgroundSrc={backgroundImage?.toURL()}>
        {Hero}
        <S.ColumnContainer>
          <S.LeftColumn>
            <ErrorBoundary>
              <TabContainer
                tabs={tabs}
                isLeftColumn
                slug={data.slug}
                tabTitle={searchParams.get('leftColumn')}
                onTabChange={(tabTitle) => updateSearchParams('leftColumn', tabTitle)}
              />
            </ErrorBoundary>
          </S.LeftColumn>
          <S.RightColumn>
            <ErrorBoundary>
              <TabContainer
                tabs={rightColumnTabs}
                isLeftColumn={false}
                slug={data.slug}
                tabTitle={searchParams.get('rightColumn')}
                onTabChange={(tabTitle) => updateSearchParams('rightColumn', tabTitle)}
              />
            </ErrorBoundary>
          </S.RightColumn>
        </S.ColumnContainer>
        <S.FeedbackContainer>
          <FeedbackSurvey contentUrl={contentUrl} />
        </S.FeedbackContainer>
      </S.PageContent>
    </Layout>
  );
};
